import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '@/app/session';
import { Icon } from '@/features/common/Icon';
import { StatusLine } from '@/features/common/StatusLine';
import { useStatusLine } from '@/features/common/useStatusLine';
import { communityApi } from '@/features/publicsite/community/api/communityApi';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import type { QaThread } from '@/features/publicsite/types/community/QaThread';
import { publicCommunity } from '@/i18n/dictionaries/publicCommunity';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * An expert Q&A session's thread (ToR §6.13): every question asked, its answer once the expert
 * gives one, and a form to ask another. Reading is anonymous — the thread is public — asking is
 * not, the same "read free, act signed-in" split /instruiri's enrolment and this module's own
 * topic replies use.
 */
export function QaSessionPage() {
  const { id = '' } = useParams();
  const { user } = useSession();
  const { t } = useTranslation(publicCommunity);
  const [thread, setThread] = useState<QaThread | null>(null);
  const [missing, setMissing] = useState(false);
  const [question, setQuestion] = useState('');
  const { status, statusKind, succeed, fail } = useStatusLine();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Called again after a mutation (a question asked) — not an effect dependency, since nothing
  // but askQuestion below ever calls it outside the mount effect.
  const refresh = () => communityApi.qaThread(id).then(setThread);

  useEffect(() => {
    let live = true;
    setMissing(false);
    communityApi
      .qaThread(id)
      .then((th) => {
        if (live) setThread(th);
      })
      .catch(() => {
        if (live) setMissing(true);
      });
    return () => {
      live = false;
    };
  }, [id]);

  usePublicPageTitle(missing ? t('notFound') : (thread?.title ?? null));

  const askQuestion = async () => {
    if (!question.trim()) return;
    await communityApi.ask(id, question.trim());
    setQuestion('');
    succeed(t('questionSubmitted'));
    refresh();
  };

  // The backend is the actual authority on who may answer (CommunityService.answer refuses
  // anyone but the session's own expert) — the form is offered to any signed-in reader rather
  // than trying to guess that from here, and a refusal surfaces as the one line the reader needs.
  const submitAnswer = async (questionId: string) => {
    const answer = drafts[questionId]?.trim();
    if (!answer) return;
    try {
      await communityApi.answer(questionId, answer);
      setDrafts((prev) => ({ ...prev, [questionId]: '' }));
      succeed(t('answerSubmitted'));
      refresh();
    } catch {
      fail(t('answerError'));
    }
  };

  if (missing) {
    return (
      <section className="container section">
        <h1>{t('notFound')}</h1>
        <p>
          <Link to="/comunitate">{t('backToCommunity')}</Link>
        </p>
      </section>
    );
  }
  if (!thread) {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }

  return (
    <section className="container section">
      <Link className="detail-back" to="/comunitate">
        <Icon name="chevron-left" className="detail-back-icon" />
        {t('backToCommunity')}
      </Link>
      {/* No status label here either — see CommunityPage.tsx: nothing ever closes a session, so
          it would read "Deschisă" on every one that exists. */}
      <h1>{thread.title}</h1>
      <StatusLine status={status} kind={statusKind} />

      {thread.questions.length === 0 && <p className="empty-state">{t('noQuestions')}</p>}
      {thread.questions.length > 0 && (
        <div className="doc-list workqueue-list">
          {thread.questions.map((q) => (
            <div className="doc-row qa-question" key={q.id}>
              <div className="doc-row__title qa-question__text">{q.question}</div>
              <p className="muted qa-question__answer">{q.answer ?? t('awaitingAnswer')}</p>
              {q.answer === null && user && (
                <p className="reply-form reply-form--inset">
                  <input
                    className="reply-form__input"
                    placeholder={t('answerPlaceholder')}
                    value={drafts[q.id] ?? ''}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  />
                  <button className="btn btn--ghost" onClick={() => void submitAnswer(q.id)}>
                    {t('answerAction')}
                  </button>
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {thread.status === 'OPEN' &&
        (user ? (
          <p className="reply-form reply-form--top reply-form--inset-lg">
            <textarea
              className="reply-form__input"
              rows={3}
              placeholder={t('questionPlaceholder')}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
            <button className="btn btn--primary" onClick={() => void askQuestion()}>
              {t('askAction')}
            </button>
          </p>
        ) : (
          <p>
            <a className="btn btn--primary btn--md" href="/saml/authenticate/mpass">
              <img className="btn__logo" src="/img/gov/mpass.svg" alt="" aria-hidden="true" />
              <span>{t('signInToAsk')}</span>
            </a>
          </p>
        ))}
    </section>
  );
}
